/**
 * Tema da interface (`user_settings.chart_preferences`).
 */
import { toRepositoryError } from "@/shared/repositories/common/repositoryError";
import {
  getCurrentUserId,
  toJsonObject,
} from "@/shared/repositories/settings/userSettingsCommon";
import { supabase } from "@/shared/supabase/client";
import type { Json } from "@/shared/supabase/database.types";

export type ThemeSettingsResult =
  | { status: "ok"; theme: "light" | "dark" | "system" | null }
  | { status: "error" };

export const themeRepository = {
  /** `theme: null` com `ok` = sem linha ou sem tema salvo; `error` = falha de rede/API. */
  getTheme: async (): Promise<ThemeSettingsResult> => {
    try {
      const userId = await getCurrentUserId();
      const { data, error } = await supabase
        .from("user_settings")
        .select("chart_preferences")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) return { status: "error" };
      if (!data) return { status: "ok", theme: null };
      const prefs = toJsonObject(data?.chart_preferences);
      const val = prefs.theme ?? prefs.colorScheme;
      if (val === "dark" || val === "light" || val === "system") {
        return { status: "ok", theme: val };
      }
      return { status: "ok", theme: null };
    } catch {
      return { status: "error" };
    }
  },

  updateTheme: async (theme: "light" | "dark" | "system"): Promise<void> => {
    try {
      const userId = await getCurrentUserId();
      const { data, error } = await supabase
        .from("user_settings")
        .select("chart_preferences")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) {
        throw toRepositoryError(
          error,
          "Não foi possível carregar preferências.",
        );
      }
      const currentPreferences = toJsonObject(data?.chart_preferences);
      const mergedPreferences: Json = {
        ...currentPreferences,
        theme,
      };
      const { error: saveError } = await supabase
        .from("user_settings")
        .upsert(
          { user_id: userId, chart_preferences: mergedPreferences },
          { onConflict: "user_id" },
        );
      if (saveError) {
        throw toRepositoryError(
          saveError,
          "Não foi possível salvar preferência de tema.",
        );
      }
    } catch (error) {
      throw toRepositoryError(error, "Falha ao salvar preferência de tema.");
    }
  },
};
